import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { contentTypeLabels } from '@/mocks/movies';

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + items.length) % items.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={item.backdrop || item.cover}
            alt={`${item.title} 剧照 - 亚洲中文字幕在线观看`}
            title={`${item.title} - 亚洲中文字幕在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-2xl">
            {items.map((item, index) => (
              <div
                key={item.id}
                className={index === active ? 'block animate-fade-up' : 'hidden'}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 rounded-md bg-accent-500 text-white text-xs font-bold">
                    {item.rating.toFixed(1)} 分
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-white/15 text-white text-xs backdrop-blur">
                    {contentTypeLabels[item.type]}
                  </span>
                  <span className="text-white/80 text-xs">{item.region} · {item.year}</span>
                </div>
                <h2 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-3">
                  {item.title}
                </h2>
                <p className="text-white/85 text-sm md:text-base leading-relaxed mb-6 max-w-xl">
                  {item.summary}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={`/detail/${item.id}`}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-play-fill text-xl"></i>
                    立即观看
                  </Link>
                  <Link
                    to={`/detail/${item.id}`}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-white/12 hover:bg-white/20 text-white text-sm font-medium whitespace-nowrap backdrop-blur transition-colors cursor-pointer"
                  >
                    <i className="ri-information-line text-lg"></i>
                    查看详情
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="hidden md:flex absolute z-20 left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="hidden md:flex absolute z-20 right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div
        className="absolute z-20 bottom-5 left-0 right-0 flex items-center justify-center gap-2"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? 'w-7 bg-accent-500' : 'w-1.5 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}